import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchQuotes } from "../utils/shopApi";
import { NAV } from "../Navbar/navbarTheme";
import AuthFormLayout from "./AuthFormLayout";
import {
  AUTH_PRIMARY,
  authAlertSx,
  authFooterActionsSx,
  authFooterSecondaryButtonSx,
  authPageSx,
  authSecondaryButtonSx,
  authSubmitButtonSx,
} from "./authStyles";

const LINE = NAV.line;

const sectionLabelSx = {
  fontSize: "0.75rem",
  fontWeight: 800,
  color: NAV.inkMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.5,
};

const STATUS_LABELS = {
  pending: "Pending",
  in_review: "In review",
  quoted: "Quoted",
};

const STATUS_STYLES = {
  pending: {
    chip: { bgcolor: "rgba(245, 158, 11, 0.12)", color: "#b45309" },
  },
  in_review: {
    chip: { bgcolor: "rgba(30, 90, 158, 0.1)", color: AUTH_PRIMARY },
  },
  quoted: {
    chip: { bgcolor: "rgba(22, 163, 74, 0.12)", color: "#15803d" },
  },
};

const quoteCardSx = {
  borderRadius: 2,
  border: `1px solid ${LINE}`,
  bgcolor: NAV.chipRowBg,
  overflow: "hidden",
};

const STORAGE_BASE = "https://api.uktomars.co.uk/storage";

function getLineImage(line) {
  const product = line?.product;
  if (product?.image_url) return product.image_url;
  if (typeof product?.image === "string" && product.image.startsWith("http")) {
    return product.image;
  }
  if (typeof product?.image === "string" && product.image) {
    return `${STORAGE_BASE}/${product.image}`;
  }
  return "";
}

function getLineProductPath(line) {
  const productId = line?.product_id ?? line?.product?.id;
  return productId != null ? `/products/${productId}` : null;
}

function hasLinePrice(line) {
  return line?.unit_price != null && String(line.unit_price).trim() !== "";
}

function parsePrice(value) {
  if (value == null || value === "") return 0;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
}

function formatGBP(amount) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getLineSubtotal(line) {
  if (!hasLinePrice(line)) return null;
  const qty = Number(line?.quantity) || 0;
  const unit = parsePrice(line?.unit_price);
  return qty * unit;
}

function getQuotePricing(lines) {
  const items = Array.isArray(lines) ? lines : [];
  const pricedLines = items.filter(hasLinePrice);
  const total = pricedLines.reduce(
    (sum, line) => sum + (getLineSubtotal(line) ?? 0),
    0,
  );

  return {
    total,
    pricedCount: pricedLines.length,
    lineCount: items.length,
    hasUnpricedLines: pricedLines.length < items.length,
  };
}

function formatLineSubtotal(line) {
  const subtotal = getLineSubtotal(line);
  if (subtotal == null) return "Awaiting quote";
  return formatGBP(subtotal);
}

function getLineProductCode(line) {
  const product = line?.product;
  const code = product?.sku || product?.code;
  return code ? String(code).trim() : "—";
}

function formatGrossWeight(value) {
  return formatWeight(parseWeight(value));
}

function parseWeight(value) {
  if (value == null || value === "") return null;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

function formatWeight(kg) {
  if (kg == null) return "—";
  return `${new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(kg)} kg`;
}

function getLineTotalWeightKg(line) {
  const unitWeight = parseWeight(line?.product?.gross_weight);
  if (unitWeight == null) return null;
  const qty = Number(line?.quantity) || 0;
  return unitWeight * qty;
}

function getQuoteEstimatedWeight(lines) {
  const items = Array.isArray(lines) ? lines : [];
  let total = 0;
  let hasWeight = false;

  for (const line of items) {
    const lineWeight = getLineTotalWeightKg(line);
    if (lineWeight != null) {
      total += lineWeight;
      hasWeight = true;
    }
  }

  return hasWeight ? total : null;
}

const detailCellSx = {
  border: `1px solid ${LINE}`,
  borderRadius: 1.5,
  bgcolor: NAV.chipRowBg,
  py: 0.85,
  px: 1,
  minWidth: 0,
  textAlign: "center",
};

const detailCell = (label, value) => (
  <Box sx={detailCellSx}>
    <Typography
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        color: NAV.inkMuted,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        mb: 0.35,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 700,
        color: AUTH_PRIMARY,
        lineHeight: 1.3,
        wordBreak: "break-word",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

function formatQuantity(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isFinite(n)) return String(n);
  return String(value).trim() || "—";
}

const QuoteLineRow = ({ line }) => {
  const imageUrl = getLineImage(line);
  const productPath = getLineProductPath(line);
  const product = line?.product;
  const categoryName = product?.category?.name;
  const caseSize = product?.case_size;
  const grossWeight = product?.gross_weight;
  const lineTotalWeight = getLineTotalWeightKg(line);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: NAV.surface,
        border: `1px solid ${LINE}`,
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.25 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: 1.5,
            bgcolor: NAV.chipRowBg,
            border: `1px solid ${LINE}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={line.product_name || "Product"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                p: 0.5,
              }}
            />
          ) : (
            <Inventory2OutlinedIcon
              sx={{ color: NAV.inkMuted, fontSize: 28 }}
            />
          )}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "flex-start" }}
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              {productPath ? (
                <Typography
                  component={Link}
                  to={productPath}
                  state={{ product }}
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.875rem",
                    color: AUTH_PRIMARY,
                    lineHeight: 1.35,
                    textDecoration: "none",
                    display: "block",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {line.product_name || "Unnamed product"}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.875rem",
                    color: NAV.ink,
                    lineHeight: 1.35,
                  }}
                >
                  {line.product_name || "Unnamed product"}
                </Typography>
              )}
              {categoryName ? (
                <Typography
                  sx={{ fontSize: "0.75rem", color: NAV.inkMuted, mt: 0.35 }}
                >
                  {categoryName}
                </Typography>
              ) : null}
            </Box>
            <Chip
              label={formatLineSubtotal(line)}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: hasLinePrice(line)
                  ? NAV.navyMuted
                  : "rgba(245, 158, 11, 0.12)",
                color: hasLinePrice(line) ? AUTH_PRIMARY : "#b45309",
                flexShrink: 0,
              }}
            />
          </Stack>
        </Box>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 0.75,
        }}
      >
        {detailCell("Quantity", formatQuantity(line.quantity))}
        {detailCell("Product code", getLineProductCode(line))}
        {detailCell("Case size", caseSize || "—")}
        {detailCell("Gross weight", formatGrossWeight(grossWeight))}
      </Box>

      <Box
        sx={{
          mt: 1.25,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          bgcolor: NAV.navyMuted,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: AUTH_PRIMARY,
            fontSize: "0.9375rem",
          }}
        >
          Total weight: {formatWeight(lineTotalWeight)}
        </Typography>
      </Box>
    </Box>
  );
};

const QuoteCard = ({ quote }) => {
  const [expanded, setExpanded] = useState(false);
  const lines = Array.isArray(quote.lines) ? quote.lines : [];
  const statusKey = String(quote.status ?? "").toLowerCase();
  const statusLabel = STATUS_LABELS[statusKey] ?? quote.status ?? "Unknown";
  const statusStyle = STATUS_STYLES[statusKey] ?? STATUS_STYLES.pending;
  const pricing = getQuotePricing(lines);
  const estimatedTotalWeight = getQuoteEstimatedWeight(lines);

  const pricingLabel = (() => {
    if (pricing.pricedCount === 0) return null;
    if (pricing.hasUnpricedLines) {
      return `${formatGBP(pricing.total)}+`;
    }
    return formatGBP(pricing.total);
  })();

  return (
    <Box sx={quoteCardSx}>
      <Box sx={{ p: 1.75 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ mb: quote.customer_note || lines.length > 0 ? 1.5 : 0 }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: `linear-gradient(135deg, ${AUTH_PRIMARY} 0%, #163a5e 100%)`,
                flexShrink: 0,
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.9375rem",
                  color: NAV.ink,
                  lineHeight: 1.35,
                }}
              >
                {quote.reference || `Quote #${quote.id}`}
              </Typography>
              <Typography
                sx={{ fontSize: "0.8125rem", color: NAV.inkMuted, mt: 0.35 }}
              >
                Submitted {formatDate(quote.created_at)}
                {quote.updated_at && quote.updated_at !== quote.created_at
                  ? ` · Updated ${formatDate(quote.updated_at)}`
                  : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            {pricingLabel ? (
              <Chip
                label={pricingLabel}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: NAV.navyMuted,
                  color: AUTH_PRIMARY,
                }}
              />
            ) : lines.length > 0 ? (
              <Chip
                label="Awaiting quote"
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: "rgba(245, 158, 11, 0.12)",
                  color: "#b45309",
                }}
              />
            ) : null}
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                height: 26,
                ...statusStyle.chip,
              }}
            />
          </Stack>
        </Stack>

        {quote.customer_note ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.5,
              mb: lines.length > 0 ? 1.5 : 0,
              borderRadius: 2,
              bgcolor: NAV.chipRowBg,
              border: `1px solid ${LINE}`,
            }}
          >
            <NotesOutlinedIcon
              sx={{
                fontSize: 18,
                color: NAV.inkMuted,
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...sectionLabelSx, mb: 0.25 }}>Note</Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: NAV.inkMuted,
                  lineHeight: 1.6,
                }}
              >
                {quote.customer_note}
              </Typography>
            </Box>
          </Box>
        ) : null}

        {lines.length > 0 ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mb: 1.5,
              py: 1,
              px: 1.25,
              borderRadius: 2,
              bgcolor: NAV.chipRowBg,
              border: `1px solid ${LINE}`,
            }}
          >
            <Typography sx={{ fontWeight: 800, color: AUTH_PRIMARY }}>
              Estimated total weight
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: AUTH_PRIMARY,
                fontSize: "1.125rem",
              }}
            >
              {formatWeight(estimatedTotalWeight)}
            </Typography>
          </Stack>
        ) : null}

        {lines.length > 0 ? (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                ...authSecondaryButtonSx,
                mt: quote.customer_note ? 0 : 0.5,
                mb: 0,
              }}
            >
              {expanded ? "Hide" : "View"} {lines.length} line item
              {lines.length === 1 ? "" : "s"}
            </Button>

            <Collapse in={expanded}>
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                <Divider sx={{ borderColor: LINE }} />
                {lines.map((line) => (
                  <QuoteLineRow
                    key={line.id ?? `${line.product_slug}-${line.quantity}`}
                    line={line}
                  />
                ))}
              </Stack>
            </Collapse>
          </>
        ) : (
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: NAV.inkMuted,
              fontStyle: "italic",
              mt: 0.5,
            }}
          >
            No line items on this request.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const QuoteHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSelector((state) => state.auth);

  const [quotes, setQuotes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!token) {
      navigate("/SignIn", { replace: true });
    }
  }, [token, navigate]);

  const loadQuotes = useCallback(
    async (pageNumber) => {
      setLoading(true);
      setError("");
      try {
        const payload = await fetchQuotes({ page: pageNumber });
        setQuotes(Array.isArray(payload?.data) ? payload.data : []);
        setMeta(payload?.meta ?? null);
      } catch (loadError) {
        if (loadError?.code === "AUTH_REQUIRED") {
          navigate("/SignIn", { replace: true });
          return;
        }
        setError(
          loadError?.message ||
            "Unable to load your quote history right now. Please try again later.",
        );
        setQuotes([]);
        setMeta(null);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (!token) return;
    loadQuotes(page);
  }, [token, page, loadQuotes]);

  const statusCounts = useMemo(() => {
    return quotes.reduce((acc, quote) => {
      const key = String(quote.status ?? "").toLowerCase() || "pending";
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
  }, [quotes]);

  if (!token) {
    return null;
  }

  if (loading && quotes.length === 0 && !error) {
    return (
      <Box
        component="main"
        sx={{
          ...authPageSx,
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: AUTH_PRIMARY }} />
      </Box>
    );
  }

  const lastPage = meta?.last_page ?? 1;
  const currentPage = meta?.current_page ?? page;
  const total = meta?.total ?? quotes.length;

  return (
    <AuthFormLayout
      wide
      maxWidth={720}
      title="Quote history"
      subtitle="View your submitted quote requests and their current status."
      icon={HistoryOutlinedIcon}
      footer={
        <Stack spacing={1.5} sx={authFooterActionsSx}>
          <Button
            component={Link}
            to="/Profile"
            variant="outlined"
            fullWidth
            sx={authFooterSecondaryButtonSx}
          >
            Back to profile
          </Button>
        </Stack>
      }
    >
      {total > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: NAV.chipRowBg,
            border: `1px solid ${LINE}`,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              background: `linear-gradient(135deg, ${AUTH_PRIMARY} 0%, #163a5e 100%)`,
              flexShrink: 0,
            }}
          >
            <HistoryOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 800, color: NAV.ink, lineHeight: 1.3 }}
            >
              Your requests
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: NAV.inkMuted,
                mt: 0.25,
              }}
            >
              {total} quote request{total === 1 ? "" : "s"} on record
            </Typography>
          </Box>
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={authAlertSx}>
          {error}
        </Alert>
      ) : null}

      {!error && quotes.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography sx={{ color: NAV.inkMuted, mb: 2, lineHeight: 1.6 }}>
            You have not submitted any quote requests yet. Browse products and
            add them to your basket to request a quote.
          </Typography>
          <Button
            component={Link}
            to="/products"
            variant="contained"
            sx={authSubmitButtonSx}
          >
            Browse products
          </Button>
        </Box>
      ) : null}

      {!error && quotes.length > 0 ? (
        <Stack spacing={2}>
          {Object.keys(statusCounts).length > 1 ? (
            <Box>
              <Typography sx={sectionLabelSx}>Summary</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries(statusCounts).map(([key, count]) => {
                  const style = STATUS_STYLES[key] ?? STATUS_STYLES.pending;
                  return (
                    <Chip
                      key={key}
                      label={`${STATUS_LABELS[key] ?? key}: ${count}`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        ...style.chip,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1 }}>Requests</Typography>
            <Stack spacing={1.5}>
              {quotes.map((quote) => (
                <QuoteCard key={quote.id} quote={quote} />
              ))}
            </Stack>
          </Box>

          {lastPage > 1 ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{
                pt: 0.5,
                px: 0.5,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage <= 1 || loading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                startIcon={<ChevronLeftIcon />}
                sx={authFooterSecondaryButtonSx}
              >
                Previous
              </Button>
              <Typography sx={{ fontSize: "0.8125rem", color: NAV.inkMuted }}>
                Page {currentPage} of {lastPage}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPage >= lastPage || loading}
                onClick={() => setPage((prev) => prev + 1)}
                endIcon={<ChevronRightIcon />}
                sx={authFooterSecondaryButtonSx}
              >
                Next
              </Button>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </AuthFormLayout>
  );
};

export default QuoteHistoryPage;
